/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly coupled to the Product template.
 *
 * @namespace product
 */
import {formatMoney} from '@shopify/theme-currency';
import {getUrlWithVariant, ProductForm} from '@shopify/theme-product-form';
import {register} from '@shopify/theme-sections';
import Flickity from 'flickity-as-nav-for';
import customSelect from 'custom-select';

import 'flickity-imagesloaded';
import 'flickity-fullscreen';

import initHowToUseVideos from '../components/product-how-to-use';

const selectors = {
  addToCart: '[data-add-to-cart]',
  comparePrice: '[data-product-compare-at-price]',
  priceWrapper: '[data-price-wrapper]',
  formElement: '[data-product-form]',
  productJson: '[data-product-json]',
  productImageWrapper: '[data-product-image-wrapper]',
  productPrice: '[data-price-product]',
  tagDiscountedPrice: '[data-price-product-discounted]',
  afterpayAmount: '[data-afterpay-amount]',
  afterpayPaymentsNo: '[data-afterpay-payments-no]',
  wishlistBtn: '[data-pdp-wishlist-btn]',
  subscriptionDiscount: '[data-subscription-discount]',
  upsellsWrap: '[data-product-upsells-wrap]',
  upsellAdd: '[data-upsell-add]',
  productResultsMainSlider: '.product-results__before-after-slider',
  productResultsThumbnailSlider: '.product-results__thumbnail-slider',
  howToUseDrawer: '[data-drawer-how-to-use]',
};

const cssClasses = {
  hide: 'hide',
  onsale: 'product__price--on-sale',
  hidden: 'hidden',
};

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

export default register('product', {
  onLoad() {
    // Register the product form using @shopify/theme-product-form
    const formElement = this.container.querySelector(selectors.formElement);
    const productJSON = window.productJSON || {};
    const options = {
      onOptionChange: this.onOptionChange.bind(this),
      onFormSubmit: this.onFormSubmit.bind(this),
      // theme-product-form requires callbacks to be passed
      // for all four form events.
      onQuantityChange: () => true,
      onPropertyChange: () => true,
    };
    this.productForm = new ProductForm(formElement, productJSON, options);

    // Parse the settings string passed through from liquid
    const settingsData = this.container.dataset.sectionSettings || '{}';
    this.sectionSettings = JSON.parse(settingsData);

    // If URL contains ?variant= param, get the selected variant ID
    const selectedVariant = this.container.dataset.selectedVariant;

    const externalVideos = this.container.querySelectorAll('[data-video-index] > iframe');
    if (externalVideos.length > 0) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = 'async';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = function () {
        externalVideos.forEach(function (playerIframe) {
          const name = `player-${playerIframe.id}`;
          window[name] = new YT.Player(playerIframe.id); // eslint-disable-line no-undef
        });
      };
    }

    // Look for a product image slideshow.
    this.slideShow = this.container.querySelector('.product__slideshow');

    // Initialise the slider
    this.slider = null;
    if (this.slideShow) {
      const container = this.container;
      const slideshow = this.slideShow;
      const settings = this.sectionSettings;
      const updateFeaturedImage = this.updateFeaturedImage.bind(this);

      Flickity.prototype._touchActionValue = 'pan-y pinch-zoom';

      this.slider = new Flickity(slideshow, {
        draggable: '>1',
        prevNextButtons: false,
        pageDots: settings.include_pager_dots,
        watchCSS: true,
        wrapAround: settings.infinite,
        dragThreshold: 1,
        friction: 0.22,
        autoPlay: Number(settings.autoplay_speed * 1000),
        fullscreen: true,
        on: {
          ready() {
            // If enabled, connect slider previous/next buttons
            this.previousButton = container.querySelector('.controls-container--main .btn--control-prev');
            this.nextButton = container.querySelector('.controls-container--main .btn--control-next');

            if (this.previousButton) {
              this.previousButton.addEventListener(
                'click',
                function () {
                  this.previous(settings.infinite, false);
                }.bind(this)
              );
            }

            if (this.nextButton) {
              this.nextButton.addEventListener(
                'click',
                function () {
                  this.next(settings.infinite, false);
                }.bind(this)
              );
            }

            // If enabled, connect slider zoom in/out buttons
            this.sliderWrapper = container.querySelector('.product__media');
            this.zoomTrigger = container.querySelector('[data-trigger-product-carousel-zoom]');

            const productImageCarouselZoom = () => {
              if (!this.zoomTrigger) {
                return;
              }
              this.zoomTrigger.addEventListener('click', () => {
                if (this.isAnimating) return;

                if (matchMedia('screen and (max-width: 767px)').matches) {
                  return;
                }
                if (!this.sliderWrapper) {
                  return;
                }
                if (this.sliderWrapper.getAttribute('data-trigger-product-carousel-zoom') === 'true') {
                  this.exitFullscreen();
                  this.sliderWrapper.setAttribute('data-trigger-product-carousel-zoom', 'false');
                } else {
                  this.viewFullscreen();
                  this.sliderWrapper.setAttribute('data-trigger-product-carousel-zoom', 'true');
                }
              });
            };

            productImageCarouselZoom();

            // Stop slider drag events from impacting 3D model interaction
            const modelViewers = this.slider.querySelectorAll('model-viewer');
            if (modelViewers.length > 0) {
              const modelStopPropagation = (event) => event.stopPropagation();
              modelViewers.forEach((modelViewer) => {
                modelViewer.addEventListener('mousedown', modelStopPropagation);
                modelViewer.addEventListener('touchstart', modelStopPropagation);
                modelViewer.addEventListener('pointerdown', modelStopPropagation);
              });
            }

            // Select the slider image matching the current variant
            updateFeaturedImage(selectedVariant ? selectedVariant : null, this);
          },

          change(index) {
            document.body.classList.add('flickity-is-animating');

            const videoMedia = this.slider.querySelectorAll('video');
            if (videoMedia.length > 0) {
              videoMedia.forEach(function (video) {
                video.pause();
              });
            }

            const externalVideosEl = this.slider.querySelectorAll('[data-video-index] > iframe');
            if (externalVideosEl.length > 0) {
              externalVideosEl.forEach(function (iframe) {
                const name = `player-${iframe.id}`;
                if (window[name].stopVideo) {
                  window[name].stopVideo();
                }
              });
            }

            const slideCount = this.slider.querySelectorAll('.product__media-item').length - 1;
            if (slideCount) {
              if (index === slideCount) {
                this.previousButton.removeAttribute('disabled', 'disabled');
                this.nextButton.setAttribute('disabled', 'disabled');
              } else if (index === 0) {
                this.nextButton.removeAttribute('disabled', 'disabled');
                this.previousButton.setAttribute('disabled', 'disabled');
              } else {
                this.nextButton.removeAttribute('disabled', 'disabled');
                this.previousButton.removeAttribute('disabled', 'disabled');
              }
            }
          },

          settle() {
            document.body.classList.remove('flickity-is-animating');
          },
        },
      });

      const productCarouselZoomCursor = () => {
        const customCursor = document.querySelector('.zoom-cursor');
        const sliderControls = document.querySelectorAll('.product__media .btn--control');
        const currentSlide = document.querySelector('.product__slideshow .product__media-item.is-selected');

        if (currentSlide && customCursor && sliderControls) {
          const handleCustomCursor = (evt) => {
            customCursor.style.top = `${evt.pageY}px`;
            customCursor.style.left = `${evt.pageX}px`;
          };
          currentSlide.addEventListener('mousemove', handleCustomCursor);
          currentSlide.addEventListener('mouseenter', () => document.body.classList.add('zoom-cursor--visible'));
          currentSlide.addEventListener('mouseleave', () => document.body.classList.remove('zoom-cursor--visible'));
          sliderControls.forEach((control) => {
            control.addEventListener('mouseenter', () => {
              document.body.classList.remove('zoom-cursor--visible');
            });
          });
        }
      };

      productCarouselZoomCursor();
      this.slider.on('change', function () {
        productCarouselZoomCursor();
      });
    }

    // Toggle side drawer
    const sideDrawerToggles = document.querySelectorAll('[data-toggle-side-drawer]');
    const sideDrawer = document.querySelector('.side__drawer');
    const sideDrawerContent = document.querySelectorAll('[data-side-drawer-content]');

    if (sideDrawerToggles.length > 0 && sideDrawer) {
      sideDrawerToggles.forEach(function (el) {
        el.addEventListener('click', function () {
          const sideDrawerCloseButton = sideDrawer.querySelector('.side__drawer-close');
          if (sideDrawer.classList.contains('is-hidden')) {
            if (sideDrawerContent && sideDrawerContent.length > 0) {
              // Change side drawer content
              sideDrawerContent.forEach((contentWrapper) => {
                contentWrapper.classList.remove('is-visible');
                if (contentWrapper.dataset.sideDrawerContent === el.dataset.toggleSideDrawer) {
                  contentWrapper.classList.add('is-visible');
                  if (sideDrawerCloseButton) {
                    sideDrawerCloseButton.focus();
                  }
                }
              });
            }
            sideDrawer.classList.remove('is-hidden');
            sideDrawer.classList.add('is-visible');
            sideDrawer.setAttribute('aria-hidden', false);
            document.body.classList.add('sidedraw-is-visible');
          } else {
            sideDrawer.classList.add('is-hidden');
            sideDrawer.classList.remove('is-visible');
            sideDrawer.setAttribute('aria-hidden', true);
            document.body.classList.remove('sidedraw-is-visible');
          }
          sideDrawerCloseButton.addEventListener('click', () => {
            el.focus();
          });
        });
      });
    }

    // Product stitching
    const handleStitchedProducts = () => {
      const stitchingSelect = document.querySelector('#ProductStitchingSelect');
      if (!stitchingSelect) {
        return;
      }
      stitchingSelect.addEventListener('change', (evt) => {
        const url = evt.target.value;
        window.location.href = window.location.origin + url;
      });
    };
    handleStitchedProducts();

    const wishlistBtn = document.querySelector(selectors.wishlistBtn);
    if (wishlistBtn) {
      wishlistBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
      });
    }

    const chatTriggers = document.querySelectorAll('[data-trigger-gorgias-chat]');
    chatTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (window.GorgiasChat) window.GorgiasChat.open();
      });
    });

    const subscriptionSelect = document.querySelector('[data-recharge-select]');
    if (subscriptionSelect) {
      const validationError = `<span class="deliveryFrequencyError">Please select delivery frequency above</span>`;
      const frequencySelect = document.querySelector('.rc_block__type__autodeliver');
      customSelect('[data-recharge-select] select');
      subscriptionSelect.classList.add('appear-in');
      frequencySelect.insertAdjacentHTML('beforeend', validationError);

      const selectContainer = subscriptionSelect.querySelector('.custom-select-container');
      const selectTrigger = subscriptionSelect.querySelector('.custom-select-opener > span');

      if (selectContainer && selectTrigger) {
        const rechargeSelect = document.querySelector('[data-recharge-select] select').customSelect;
        selectTrigger.addEventListener('click', function () {
          if (selectContainer.classList.contains('is-open')) {
            setTimeout(() => {
              if (rechargeSelect) rechargeSelect.open = false;
            }, 10);
          }
        });
      }
    }

    const validationErrorMessage = document.querySelector('#rc_container .deliveryFrequencyError');
    const selectElement = document.querySelector('[data-recharge-select] select');
    const selectTarget = document.querySelector('[data-recharge-select] .custom-select-opener');

    const validateAddToCartSubmission = () => {
      const subscriptionElem = document.querySelector('.rc_radio__autodeliver');
      if (!subscriptionElem) return true;

      const isSubscriptionSelected = subscriptionElem.checked;

      selectElement.addEventListener('change', () => {
        if (!selectElement.value.length) return;
        validationErrorMessage.classList.remove('isVisible');
        selectTarget.classList.remove('validationError');
      });

      if (!isSubscriptionSelected || selectElement.value.length) return true;

      selectTarget.classList.add('validationError');
      validationErrorMessage.classList.add('isVisible');
      return false;
    };

    const oneTimePurchaseOption = document.querySelector('.rc_radio__onetime');
    if (oneTimePurchaseOption) {
      oneTimePurchaseOption.addEventListener('change', function () {
        if (!oneTimePurchaseOption.checked) return;
        validationErrorMessage.classList.remove('isVisible');
        selectTarget.classList.remove('validationError');
      });
    }

    const addToCartBtn = formElement.querySelector(selectors.addToCart);
    if (addToCartBtn && selectElement && selectTarget) {
      addToCartBtn.addEventListener('click', (evt) => {
        if (validateAddToCartSubmission()) {
          return;
        }
        evt.preventDefault();
      });
    }

    const handleUpsellAddToCart = (upsellElement) => {
      const addToBagBtn = upsellElement.querySelector(selectors.upsellAdd);
      addToBagBtn?.addEventListener('click', (event) => {
        window.sideCart.handleAddToCartEvent(event, addToBagBtn.dataset.id, 1, true);
      });
    };

    const processProductUpsells = async (upsellsParent, upsellsLimit) => {
      if (!upsellsParent) return;

      const productUpsells = upsellsParent.querySelectorAll('.product__upsell');
      if (productUpsells.length === upsellsLimit) {
        // If rendered enough products, skip Recommendations API, handle 'Add to cart' buttons
        productUpsells.forEach((upsell) => handleUpsellAddToCart(upsell));
      } else {
        // Otherwise fetch recommendations, exclude gift-cards, sort by most expensive (2)
        const productId = formElement.dataset.productid;
        const url = `${window.location.origin}/recommendations/products?product_id=${productId}&limit=10&section_id=product-upsells`;
        try {
          const response = await fetch(url);
          const text = await response.text();
          const htmlElem = document.createElement('div');
          htmlElem.innerHTML = text;
          const recommendations = [...htmlElem.querySelectorAll('.product__upsell')];

          const recommendationsWithoutGiftcards = recommendations.filter(
            (recommendation) => recommendation.dataset.productHandle.indexOf('gift-card') === -1
          );

          const recommendationsWithoutFreeProducts = recommendationsWithoutGiftcards.filter(
            (recommendation) => Number(recommendation.dataset.upsellPrice) > 0
          );

          // Only use 1st two values
          const finalRecommendations = recommendationsWithoutFreeProducts.slice(0, 2);

          let upsellsLength = productUpsells.length;
          while (upsellsLength < upsellsLimit) {
            if (!finalRecommendations.length) break;
            upsellsParent.insertAdjacentElement('beforeend', finalRecommendations.shift());
            upsellsLength++;
          }

          const upsellElements = upsellsParent.querySelectorAll('.product__upsell');
          upsellElements.forEach((upsellElement) => handleUpsellAddToCart(upsellElement));
        } catch (err) {
          window.console.log(err);
        }
      }
    };

    const productUpsellsWrap = document.querySelector(selectors.upsellsWrap);
    processProductUpsells(productUpsellsWrap, 2);

    const productResultsMainSlider = this.container.querySelector(selectors.productResultsMainSlider);
    const productResultsThumbnailSlider = this.container.querySelector(selectors.productResultsThumbnailSlider);

    if (productResultsMainSlider && productResultsThumbnailSlider) {
      const previousButton = this.container.querySelector('.btn--product-results-control-prev');
      const nextButton = this.container.querySelector('.btn--product-results-control-next');

      this.productResultsMainSlider = new Flickity(productResultsMainSlider, {
        draggable: false,
        prevNextButtons: false,
        pageDots: false,
        imagesLoaded: true,
      });

      const thumbSlider = new Flickity(productResultsThumbnailSlider, {
        draggable: false,
        asNavFor: productResultsMainSlider,
        prevNextButtons: false,
        pageDots: false,
        arrows: true,
        groupCells: false,
        contain: true,
        cellAlign: 'left',
        imagesLoaded: true,
        arrowShape: 'M 0,50 L 60,00 L 50,30 L 80,30 L 80,70 L 50,70 L 60,100 Z',
        wrapAround: true,
        on: {
          ready() {
            if (!previousButton || !nextButton) return;
            previousButton.addEventListener('click', function () {
              thumbSlider.previous();
            });
            nextButton.addEventListener('click', function () {
              thumbSlider.next();
            });
          },
        },
      });
    }

    const howToUseDrawer = this.container.parentNode.querySelector(selectors.howToUseDrawer);
    if (howToUseDrawer) {
      initHowToUseVideos(howToUseDrawer);
    }
  },

  // Event callback for option/variant switch event
  onOptionChange(event) {
    const variant = event.dataset.variant;

    /* eslint-disable no-negated-condition */
    if (!variant) {
      // The combination of selected options does not have a matching variant
      this.updateAddToCartState(null);
      this.updateProductPrices(false);
      this.updateFeaturedImage(false);
    } else if (!variant.available) {
      // The combination of selected options has a matching variant but it is
      // currently unavailable
      this.updateAddToCartState(false);
      this.updateProductPrices(true, variant.price, variant.compare_at_price);
      this.updateFeaturedImage(variant.id);
      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({path: url}, '', url);
    } else {
      // The combination of selected options has a matching variant and it is
      // available
      this.updateAddToCartState(true);
      this.updateProductPrices(true, variant.price, variant.compare_at_price);
      this.updateFeaturedImage(variant.id);
      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({path: url}, '', url);
    }
    /* eslint-enable no-negated-condition */
  },

  // Event callback for Theme Editor `section:unload` event
  onUnload() {
    this.productForm.destroy();
  },

  // Event callback for product form submit event
  onFormSubmit(event) {
    event.preventDefault();
    const variant = event.dataset.variant;
    let variantId = variant.id;
    const quantity = event.dataset.quantity;
    let productTitle = null;
    let subscriptionFrequency = null;
    let subscriptionFrequencyUnitType = null;
    let sellingPlan = null;

    if (variant && variant.id && quantity) {
      const subscriptionRadio =
        document.querySelector("[value='autodeliver']") ||
        document.querySelector("[name='purchase_type'][value='autodeliver']");
      if (subscriptionRadio && subscriptionRadio.checked) {
        // new RC version supports selling plan
        const newRcWidget = document.querySelector('.rc-widget-injection-parent');
        const productId = this.container.querySelector(selectors.formElement).dataset.productid;
        const sellingPlanSelect = newRcWidget?.querySelector(`#selling_plan_${productId}`);

        const subscriptionSelect =
          document.querySelector('#rc_shipping_interval_frequency') ||
          document.querySelector("[name='properties[shipping_interval_frequency]']");
        const subscriptionFrequencyUnits = document.querySelector('[data-subscription-frequency-units]');
        const subscriptionVariant = document.querySelector('[data-subscription-variant]');
        if (subscriptionSelect && subscriptionFrequencyUnits) {
          // legacy support - still add frequency and units into properties
          subscriptionFrequency = subscriptionSelect.value;
          subscriptionFrequencyUnitType = subscriptionFrequencyUnits.dataset.subscriptionFrequencyUnits;

          if (sellingPlanSelect) {
            const selectedPlan = ` ${subscriptionFrequency} ${subscriptionFrequencyUnitType}`.toLowerCase();

            const sellingPlanOptions = sellingPlanSelect.querySelectorAll('[data-plan-option]');
            sellingPlanOptions.forEach((ele, index) => {
              if (sellingPlan || index + 1 !== subscriptionSelect.selectedIndex) return;

              const planOption = ele.dataset.planOption.toLowerCase();
              if (planOption.includes(selectedPlan)) {
                sellingPlan = ele.value;
              }
            });
          }

          if (subscriptionVariant) {
            // new RC version uses product's actual variant id
            variantId = subscriptionVariant.getAttribute('value');

            // legacy support for variantId
            if (sellingPlan === null) {
              variantId = subscriptionVariant.dataset.subscriptionVariant;
            }
          } else if (window.subscriptionVariants) {
            const keys = Object.keys(window.subscriptionVariants);
            keys.forEach(function (key) {
              if (Number(key) === variantId) {
                variantId = parseInt(window.subscriptionVariants[key], 10);
              }
            });
          }
        }
      }

      const productTitleData = document.querySelector('[data-product-title]');
      if (productTitleData) {
        productTitle = productTitleData.dataset.productTitle;
      }

      window.sideCart.handleAddToCartEvent(
        event,
        variantId,
        quantity,
        true,
        sellingPlan,
        subscriptionFrequency,
        subscriptionFrequencyUnitType,
        productTitle
      );
    }
  },

  /**
   * Updates the DOM state of the add to cart button
   *
   * @param {boolean} enabledState - True is enabled. False is disabled. Null is disabled without prices.
   * @param {string} priceWrapper - DOM selector for prices container div
   * @param {string} button - DOM selector for add to cart button
   */
  updateAddToCartState(enabledState) {
    const addToCartBtn = this.container.querySelector(selectors.addToCart);
    const priceWrapper = this.container.querySelector(selectors.priceWrapper);

    switch (enabledState) {
      // Variant is available
      case true: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.textContent = theme.strings.addToCart;
        break;
      }
      // Variant is unavailable
      case false: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.textContent = theme.strings.soldOut;
        break;
      }
      // No matching variant
      case null: {
        priceWrapper.classList.add(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.textContent = theme.strings.unavailable;
        break;
      }
    }
  },

  /**
   * Updates the DOM with specified prices
   *
   * @param {boolean} variantExists - Selected options match a valid variant
   * @param {number} price - Selected variant price
   * @param {number} compareAtPrice - Selected variant original price
   * @param {string} productPrice - DOM selector for current price of the product
   * @param {string} comparePrice - DOM selector for original price of the product
   * @param {string} priceWrapper - DOM selector for prices container div
   */
  updateProductPrices(variantExists, price, compareAtPrice) {
    const comparePrice = this.container.querySelector(selectors.comparePrice);
    const productPrice = this.container.querySelectorAll(selectors.productPrice);
    const tagDiscountedPrice = this.container.querySelector(selectors.tagDiscountedPrice);
    const afterpayAmount = this.container.querySelector(selectors.afterpayAmount);
    const afterpayPaymentsNo = this.container.querySelector(selectors.afterpayPaymentsNo);
    const subscriptionPrice = this.container.querySelector(selectors.subscriptionPrice);
    const subscriptionDiscount = this.container.querySelector(selectors.subscriptionDiscount);
    const subscriptionDiscountValue = subscriptionDiscount
      ? Number(subscriptionDiscount.dataset.subscriptionDiscount)
      : 0;

    // No matching variant
    if (!variantExists && comparePrice) {
      comparePrice.textContent = '';
      comparePrice.classList.add(cssClasses.hide);
      return;
    }

    // Variant exists
    productPrice.forEach((priceEl) => {
      priceEl.textContent = formatMoney(price, theme.moneyWithCurrencyFormat);
    });

    if (comparePrice) {
      if (price < compareAtPrice) {
        comparePrice.textContent = formatMoney(compareAtPrice, theme.moneyWithCurrencyFormat);
        comparePrice.classList.remove(cssClasses.hide);
      } else {
        comparePrice.textContent = '';
        comparePrice.classList.add(cssClasses.hide);
      }
    }

    if (afterpayAmount) {
      // eslint-disable-next-line radix
      const paymentsNo = parseInt(afterpayPaymentsNo.textContent);
      const amount = price / paymentsNo;
      afterpayAmount.textContent = formatMoney(amount, theme.moneyWithCurrencyFormat);
    }

    if (subscriptionPrice) {
      const discountedPrice = price * subscriptionDiscountValue;
      subscriptionPrice.textContent = formatMoney(discountedPrice, theme.moneyWithCurrencyFormat);
    }

    if (tagDiscountedPrice) {
      const tagDiscountValue = tagDiscountedPrice.dataset.priceProductDiscounted / 100;
      const discountAmount = price * tagDiscountValue;
      const tagDiscountPrice = price - discountAmount;
      tagDiscountedPrice.textContent = formatMoney(tagDiscountPrice, theme.moneyWithCurrencyFormat);
    }
  },

  /**
   * Scrolls the image slider to that of the current variant
   *
   * @param {number} variantId - ID of the selected variant
   * @param {string} sliderImages - DOM selector for product image slider
   */
  updateFeaturedImage(variantId, thisSlider) {
    const id = variantId ? variantId : this.container.dataset.firstVariant;
    const slider = thisSlider ? thisSlider : this.slider;

    const sliderImages = this.container.querySelectorAll(selectors.productImageWrapper);
    if (sliderImages.length > 0) {
      for (let i = 0; i < sliderImages.length; i++) {
        const slide = sliderImages[i];
        const isVariantImage = slide.dataset.imageVariants.includes(id);

        if (isVariantImage && slider) {
          slider.select(Number(slide.dataset.slideIndex), this.sectionSettings.infinite, false);
          break;
        }
      }
    }
  },
});
